import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbDatepicker,
  NgbDatepickerConfig,
  NgbDatepickerI18n,
  NgbDatepickerModule
} from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { DateParserFormatter } from 'services/date-parser-formatter.service';
import { convertIsoStringToNgbDate } from 'util/date.utility';
import { NgClass } from '@angular/common';
import { formatNgbDate } from 'util/form.utility';
import { CustomDatepickerI18n } from 'src/app/i18n/custom-datepicker-i18n';

export interface DateRange {
  from: NgbDate | null;
  to: NgbDate | null;
}

const DATEPICKER_TOGGLE_DELAY = 100;

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [NgbDatepickerModule, FormsModule, NgClass],
  templateUrl: './date-range-picker.component.html',
  styleUrl: './date-range-picker.component.scss',
  providers: [
    { provide: NgbDateParserFormatter, useClass: DateParserFormatter },
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n }
  ] 
})
export class DateRangePickerComponent implements OnInit, OnChanges {
  @Input() from: string = '';
  @Input() to: string = '';
  @Input() disabledDates: NgbDate[] = [];
  @Input() maxDate?: NgbDateStruct;
  @Input() minDateYesterday: boolean = false;
  @Input() noMargin: boolean = false;
  @Input() pastDates: boolean = false;
  @Input() disabled: boolean = false;
  @Input() displayPosition: 'left' | 'right' | 'center' = 'center';
  @Input() todayAvailable: boolean = false;
  @Input() restrictFromDate: boolean = false;
  @Input() resetTrigger: boolean = false;
  @Output() rangeChanged: EventEmitter<{
    from: string | null;
    to: string | null;
  }> = new EventEmitter<{ from: string | null; to: string | null }>();
  formatNgbDate = formatNgbDate;
  calendar = inject(NgbCalendar);
  formatter = inject(NgbDateParserFormatter);
  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate | null = null;
  toDate: NgbDate | null = null;
  datesInRange: NgbDate[] = [];
  @ViewChild('datepicker') datepicker!: any;

  get minDate(): NgbDateStruct {
    const today = new Date();

    if (this.minDateYesterday) {
      return {
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        day: today.getDate() - 1
      };
    }

    return this.todayAvailable
      ? {
          year: today.getFullYear(),
          month: today.getMonth() + 1,
          day: today.getDate()
        }
      : {
          year: today.getFullYear(),
          month: today.getMonth() + 1,
          day: today.getDate() + 1
        };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resetTrigger'] && changes['resetTrigger'].currentValue === true) {
      this.resetDateRange();
    }
  }

  isDisabled = (date: NgbDateStruct) => {
    const isDisabled = this.disabledDates.some(
      (disabledDate) => disabledDate.year === date.year && disabledDate.month === date.month && disabledDate.day === date.day
    );

    return isDisabled;
  };

  constructor(private config: NgbDatepickerConfig) {}

  ngOnInit(): void {
    if (this.from) {
      this.fromDate = convertIsoStringToNgbDate(this.from);
    }
    if (this.to) {
      this.toDate = convertIsoStringToNgbDate(this.to);
    }
  }

  onDateSelection(date: NgbDate) {
    if (this.restrictFromDate) {
      if (this.fromDate && (!this.toDate || date.after(this.fromDate) || date.equals(this.fromDate))) {
        this.toDate = date;
      } else {
        this.toDate = null;
      }
    } else {
      if (!this.fromDate && !this.toDate) {
        this.fromDate = date;
        this.toDate = null;
      } else if (this.fromDate && !this.toDate && (date.after(this.fromDate) || date.equals(this.fromDate))) {
        this.toDate = date;
      } else {
        this.toDate = null;
        this.fromDate = date;
      }
    }

    if (this.fromDate && this.toDate) {
      if (this.datepicker && this.datepicker.toggle) {
        setTimeout(() => {
          this.datepicker.toggle();
        }, DATEPICKER_TOGGLE_DELAY);
      }
    }

    this.rangeChanged.emit({
      from: formatNgbDate(this.fromDate),
      to: formatNgbDate(this.toDate)
    });
  }

  isHovered(date: NgbDate) {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return date.equals(this.fromDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date);
  }

  validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
    const parsed = this.formatter.parse(input);
    return parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : currentValue;
  }

  ngbDateToDate(ngbDate: NgbDate): Date {
    const year = ngbDate.year;
    const month = ngbDate.month - 1; // NgbDate months are 1-indexed, JavaScript months are 0-indexed
    const day = ngbDate.day;

    return new Date(year, month, day);
  }

  resetDateRange(): void {
    this.fromDate = null;
    this.toDate = null;
    this.rangeChanged.emit({ from: null, to: null });
  }
}
